
import axios from 'axios';
import messageQueue from '../utils/messageQueue';
import ERROR_CODE from '../utils/Enum/errorCode';
import MessageConstant from '../utils/Constant/MessageConstant';
import store from '../reduxStore/reduxStore';
import { logoutUser } from '../reduxStore/actions/userActionsHelper';

const IS_DEMO = process.env.REACT_APP_ENVCONFIG === 'demo';

const apiBaseURL = process.env.REACT_APP_API_URL;

if (!apiBaseURL) {
    throw new Error('REACT_APP_API_URL is not defined in environment variables');
}

const api = axios.create({
    baseURL: apiBaseURL,
    withCredentials: true,
    timeout: 30000, // 30 seconds timeout for all API requests
    headers: {
        'Content-Type': 'application/json',
    },
});

// --- Token Management (Memory Storage) ---
let accessToken = null;
let tokenExpiryTime = null;
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
    failedQueue.forEach(prom => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
}

// Public API for token management
export const getAccessToken = () => accessToken;

export const setAccessToken = (token) => {
    accessToken = token;
    if (token) {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            tokenExpiryTime = payload.exp * 1000;
        } catch (e) {
            tokenExpiryTime = null;
        }
    } else {
        tokenExpiryTime = null;
    }
};

export const clearAccessToken = () => {
    setAccessToken(null);
};

export const isTokenExpiringSoon = () => {
    if (!tokenExpiryTime) return true;
    const now = Date.now();
    const timeRemaining = tokenExpiryTime - now;
    return timeRemaining < 60 * 1000; // Less than 60 seconds
};

api.interceptors.request.use(
    (config) => {
        const token = getAccessToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        const errorCode = error.response?.data?.code;

        // Skip alert/redirect if this is a silent refresh attempt (from App.js initialization)
        const isSilentRefresh = originalRequest.url?.includes('/users/refresh-token');

        // Check if we're on a public route (no auth required)
        const publicRoutes = ['/landing', '/pricing', '/about-us', '/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/confirmation', '/link-expired'];
        const currentPath = window.location.pathname;
        const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));

        // Handle timeout errors
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            if (!isSilentRefresh) {
                messageQueue.error(
                    'REQUEST_TIMEOUT',
                    'Request timeout. Please check your connection and try again.',
                    4
                );
            }
            return Promise.reject(error);
        }

        // Handle network errors
        if (error.message === 'Network Error' && !isSilentRefresh) {
            messageQueue.error(
                'NETWORK_ERROR',
                'Network error. Please check your connection and try again.',
                4
            );
            return Promise.reject(error);
        }

        // Handle session invalid (logged in elsewhere) - only on protected routes
        if (errorCode === ERROR_CODE.SESSION_INVALID && !isSilentRefresh && !isPublicRoute) {
            clearAccessToken();

            if (IS_DEMO) {
                store.dispatch(logoutUser.success());
                return Promise.reject(error);
            }

            // Dispatch event to trigger overlay in protected routes
            window.dispatchEvent(new Event('session-invalid'));

            // Use message queue to prevent duplicate messages
            messageQueue.warning(
                ERROR_CODE.SESSION_INVALID,
                MessageConstant.SESSION_INVALID + ' Please login again.',
                4,
                () => {
                    window.location.href = '/sign-in';
                }
            );
            return Promise.reject(error);
        }

        // Handle token expired with refresh - only attempt refresh on protected routes
        if (
            (error.response?.status === 401 || errorCode === ERROR_CODE.TOKEN_EXPIRED) &&
            !originalRequest._retry &&
            !isSilentRefresh &&
            !isPublicRoute
        ) {
            if (isRefreshing) {
                return new Promise(function (resolve, reject) {
                    failedQueue.push({ resolve, reject });
                })
                    .then(token => {
                        originalRequest.headers['Authorization'] = 'Bearer ' + token;
                        return api(originalRequest);
                    })
                    .catch(err => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                // Refresh uses httpOnly cookie automatically
                const res = await axios.post(
                    `${apiBaseURL}/users/refresh-token`,
                    {},
                    { withCredentials: true }
                );

                if (res.data.success && res.data.accessToken) {
                    setAccessToken(res.data.accessToken);
                    api.defaults.headers['Authorization'] = 'Bearer ' + res.data.accessToken;
                    processQueue(null, res.data.accessToken);
                    return api(originalRequest);
                }

                throw new Error('Token refresh failed');
            } catch (err) {
                processQueue(err, null);
                clearAccessToken();

                // In demo env: clear Redux user state so DemoRoute shows the onboarding modal again
                if (IS_DEMO) {
                    store.dispatch(logoutUser.success());
                    return Promise.reject(err);
                }

                // Only show alerts for actual authenticated requests, not during silent refresh or on public routes
                if (!isSilentRefresh && !isPublicRoute) {
                    // Dispatch event to trigger overlay in protected routes
                    window.dispatchEvent(new Event('session-invalid'));

                    // Handle different refresh errors using message queue
                    let errorCodeKey = ERROR_CODE.SESSION_INVALID;
                    let errorMessage = MessageConstant.SESSION_INVALID;

                    if (err.response?.data?.code === ERROR_CODE.SESSION_INVALID) {
                        errorCodeKey = ERROR_CODE.SESSION_INVALID;
                        errorMessage = MessageConstant.SESSION_INVALID;
                    } else if (err.response?.data?.code === ERROR_CODE.REFRESH_TOKEN_EXPIRED) {
                        errorCodeKey = ERROR_CODE.REFRESH_TOKEN_EXPIRED;
                        errorMessage = MessageConstant.SESSION_EXPIRED;
                    } else {
                        errorCodeKey = 'SESSION_EXPIRED_GENERIC';
                        errorMessage = MessageConstant.SESSION_EXPIRED_GENERIC;
                    }

                    messageQueue.warning(
                        errorCodeKey,
                        errorMessage,
                        3,
                        () => {
                            window.location.href = '/sign-in';
                        }
                    );
                }
                return Promise.reject(err);
            } finally {
                isRefreshing = false;
            }
        }
        return Promise.reject(error);
    }
);

export default api;
